import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { isPackSplit, isAutoQty, isPerBottle, basisOf, dimOf, isPackSplitC, isAutoC, isAutoLine, isVolC, defaultUnit, systemOfUnit, unitsForDim, groupLinesByBasis } from "@/lib/bom";
import ImageUpload from "@/components/ImageUpload";
import { Loader2, Plus, Trash2, ImageOff, GripVertical } from "lucide-react";

const inputCls = "rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]";
const BASIS = { per_bottle: "Per bottle", per_case: "Per case", per_pallet: "Per pallet", per_truckload: "Per truckload" };
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const emptyLine = () => ({ _id: uid(), component_id: "", type_id: "", config_type_ids: {}, config_quantities: {}, quantity: "1", qty_unit: "", basis: "per_bottle" });

function singular(word) {
  const w = (word || "").trim();
  if (!w) return "item";
  if (/ies$/i.test(w)) return w.slice(0, -3) + "y";
  if (/(ses|xes|zes|ches|shes)$/i.test(w)) return w.slice(0, -2);
  if (/ss$/i.test(w)) return w;
  if (/s$/i.test(w)) return w.slice(0, -1);
  return w;
}

const CFG_EXTRA = ["weight_per_bottle", "weight_per_case", "weight_per_pallet", "pallet_length", "pallet_width", "pallet_height", "case_length", "case_width", "case_height"];
const cfgExtras = (c = {}) => Object.fromEntries(CFG_EXTRA.map((k) => [k, c[k] != null ? String(c[k]) : ""]));

export default function ProductBOMForm({ initial, components, saving, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [category, setCategory] = useState(initial?.category || "");
  const [defaultPrice, setDefaultPrice] = useState(initial?.default_price_per_case != null ? String(initial.default_price_per_case) : "");
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [imgError, setImgError] = useState(false);
  const [error, setError] = useState("");
  const [packConfigs, setPackConfigs] = useState(
    initial?.pack_configs?.length
      ? initial.pack_configs.map((c) => ({ _id: uid(), id: c.id || ("cfg_" + uid().slice(0, 6)), label: c.label || "", bottles_per_case: c.bottles_per_case != null ? String(c.bottles_per_case) : "", cases_per_pallet: c.cases_per_pallet != null ? String(c.cases_per_pallet) : "", pallets_per_truckload: c.pallets_per_truckload != null ? String(c.pallets_per_truckload) : "", ...cfgExtras(c) }))
      : [{ _id: uid(), id: "cfg_1", label: "", bottles_per_case: "", cases_per_pallet: "", pallets_per_truckload: "", ...cfgExtras() }]
  );
  const [dragIndex, setDragIndex] = useState(null);
  const [lines, setLines] = useState(
    initial?.lines?.length
      ? groupLinesByBasis(initial.lines.map((l) => ({
          _id: uid(),
          component_id: l.component_id || "",
          type_id: l.type_id || "",
          config_type_ids: l.config_type_ids || {},
          config_quantities: Object.fromEntries(Object.entries(l.config_quantities || {}).map(([k, v]) => [k, v != null ? String(v) : ""])),
          quantity: l.quantity != null ? String(l.quantity) : "1",
          qty_unit: l.qty_unit || "",
          basis: l.basis || basisOf(components.find((c) => c.id === l.component_id)),
        })))
      : [emptyLine()]
  );

  const compById = useMemo(() => Object.fromEntries(components.map((c) => [c.id, c])), [components]);
  const hasCasePack = packConfigs.some((c) => Number(c.bottles_per_case) > 0);
  const cp0 = Number(packConfigs[0]?.bottles_per_case) || 0;
  const isPerCaseLine = (l) => ((l.basis || basisOf(compById[l.component_id])) === "per_case");
  const updateConfig = (idx, patch) => setPackConfigs((p) => p.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const addConfig = () => setPackConfigs((p) => [...p, { _id: uid(), id: "cfg_" + uid().slice(0, 6), label: "", bottles_per_case: "", cases_per_pallet: "", pallets_per_truckload: "", ...cfgExtras() }]);
  const removeConfig = (idx) => setPackConfigs((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  const perLabel = (comp, l) => { const b = (l && l.basis) || basisOf(comp); return b === "per_truckload" ? "per truckload" : b === "per_pallet" ? "per pallet" : b === "per_case" ? "per case" : "per bottle"; };
  const unitLabel = (comp, l) => {
    const d = dimOf(comp);
    const t = (comp?.types || []).find((x) => x.id === l.type_id);
    if (d === "volume") return `mL ${perLabel(comp, l)}`;
    if (d === "weight") return `${t?.weight_unit || "g"} ${perLabel(comp, l)}`;
    if (d === "length") return `${t?.display_unit || "m"} ${perLabel(comp, l)}`;
    return `each ${perLabel(comp, l)}`;
  };

  const updateLine = (i, patch) => setLines((p) => {
    const next = p.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    return "component_id" in patch || "basis" in patch ? groupLinesByBasis(next, compById) : next;
  });
  const addLine = () => setLines((p) => [...p, emptyLine()]);
  const removeLine = (i) => setLines((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : [emptyLine()]));
  const moveTo = (from, to) =>
    setLines((p) => {
      if (from == null || from === to || from < 0 || to < 0 || from >= p.length || to >= p.length) return p;
      const next = [...p];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });

  const submit = () => {
    setError("");
    if (!name.trim()) {
      setError("Product name is required.");
      return;
    }
    const cleaned = groupLinesByBasis(lines, compById)
      .filter((l) => !!l.component_id)
      .map((l) => {
        const cq = {};
        Object.entries(l.config_quantities || {}).forEach(([k, v]) => {
          if (String(v).trim() !== "" && packConfigs.some((c) => c.id === k)) cq[k] = parseFloat(v);
        });
        return {
          component_id: l.component_id,
          type_id: l.type_id || "",
          config_type_ids: l.config_type_ids || {},
          config_quantities: cq,
          type_id_12: (l.config_type_ids && l.config_type_ids[packConfigs.map((c) => c.id)[0]]) || l.type_id || "",
          type_id_24: (l.config_type_ids && l.config_type_ids[packConfigs.map((c) => c.id)[1]]) || "",
          basis: l.basis || "per_bottle",
          qty_unit: l.qty_unit || "",
          quantity: l.quantity === "" ? null : parseFloat(l.quantity),
        };
      });
    if (cleaned.length === 0) {
      setError("Add at least one component line.");
      return;
    }
    const numf = (s) => (String(s).trim() === "" ? null : parseFloat(s));
    const configs = packConfigs.map((c) => ({ id: c.id, label: c.label.trim(), bottles_per_case: numf(c.bottles_per_case), cases_per_pallet: numf(c.cases_per_pallet), pallets_per_truckload: numf(c.pallets_per_truckload), ...Object.fromEntries(CFG_EXTRA.map((k) => [k, numf(c[k])])) }));
    const c0 = configs[0] || {};
    const legacyBpp = c0.bottles_per_case && c0.cases_per_pallet ? c0.bottles_per_case * c0.cases_per_pallet : null;
    onSave({ name: name.trim(), category, default_price_per_case: defaultPrice === "" ? null : Number(defaultPrice), image_url: imageUrl.trim(), pack_configs: configs, pack_sizes_enabled: configs.length > 1, case_pack_qty: c0.bottles_per_case ?? null, bottles_per_pallet: legacyBpp, pallets_per_truckload: c0.pallets_per_truckload ?? null, lines: cleaned });
  };

  return (
    <div className="space-y-8" data-testid="bom-form">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:max-w-2xl">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Product Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Marvel 12oz Bottled Water" className={inputCls} data-testid="bom-name-input" autoFocus />
          <Label className="mt-3 block text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Category</Label>
          <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="bom-category-toggle">
            {["PET", "ALUM"].map((c) => (
              <button key={c} type="button" onClick={() => setCategory(category === c ? "" : c)}
                className={`rounded-sm px-4 py-1.5 text-xs font-semibold transition-colors ${category === c ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`bom-category-${c.toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
          <Label className="mt-3 block text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Default Price / Case (USD)</Label>
          <Input type="text" inputMode="decimal" value={defaultPrice} onChange={(e) => setDefaultPrice(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 5.50 (optional)" className={inputCls + " max-w-[180px]"} data-testid="bom-default-price" />
          <p className="text-[10px] text-slate-400">Auto-fills the price on new Manufacturing Orders (used on Production Order documents). Can be overridden per MO.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Product Photo</Label>
          <ImageUpload value={imageUrl} onChange={(v) => { setImageUrl(v); setImgError(false); }} testId="bom-image-upload" />
        </div>
      </div>

      <div className="space-y-3 sm:max-w-3xl" data-testid="pack-configs">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Packing Configurations</Label>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addConfig} data-testid="add-pack-config"><Plus className="mr-1 h-3.5 w-3.5" /> Add configuration</Button>
        </div>
        <div className="hidden gap-3 px-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 sm:grid sm:grid-cols-[1fr_1fr_1fr_1fr_40px]">
          <span>Label</span><span>Bottles / Case</span><span>Cases / Pallet</span><span>Pallets / Truckload</span><span />
        </div>
        {packConfigs.map((c, ci) => (
          <div key={c._id} className="space-y-2 rounded-sm border border-slate-100 p-2 sm:border-0 sm:p-0" data-testid={`pack-config-${ci}`}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_40px] sm:items-center">
              <Input value={c.label} onChange={(e) => updateConfig(ci, { label: e.target.value })} placeholder="e.g. 12-Pack" className={inputCls} data-testid={`config-label-${ci}`} />
              <Input type="text" inputMode="decimal" value={c.bottles_per_case} onChange={(e) => updateConfig(ci, { bottles_per_case: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="e.g. 12" className={inputCls} data-testid={`config-bpc-${ci}`} />
              <Input type="text" inputMode="decimal" value={c.cases_per_pallet} onChange={(e) => updateConfig(ci, { cases_per_pallet: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="e.g. 168" className={inputCls} data-testid={`config-cpp-${ci}`} />
              <Input type="text" inputMode="decimal" value={c.pallets_per_truckload} onChange={(e) => updateConfig(ci, { pallets_per_truckload: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="e.g. 22" className={inputCls} data-testid={`config-ppt-${ci}`} />
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => removeConfig(ci)} disabled={packConfigs.length <= 1} data-testid={`remove-pack-config-${ci}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_40px] sm:items-center">
              {[["weight_per_bottle", "Weight / Bottle (lbs)"], ["weight_per_case", "Weight / Case (lbs)"], ["weight_per_pallet", "Weight / Pallet (lbs)"]].map(([k, ph]) => (
                <Input key={k} type="text" inputMode="decimal" value={c[k]} onChange={(e) => updateConfig(ci, { [k]: e.target.value.replace(/[^0-9.]/g, "") })} placeholder={ph} className={inputCls} data-testid={`config-${k}-${ci}`} />
              ))}
              <span className="hidden sm:block" />
              <span />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_40px] sm:items-center">
              <div className="col-span-1 flex items-center gap-1 sm:col-span-2">
                {[["case_length", "Case L"], ["case_width", "W"], ["case_height", "H"]].map(([k, ph], j) => (
                  <span key={k} className="flex flex-1 items-center gap-1">
                    {j > 0 && <span className="text-xs text-slate-400">×</span>}
                    <Input type="text" inputMode="decimal" value={c[k]} onChange={(e) => updateConfig(ci, { [k]: e.target.value.replace(/[^0-9.]/g, "") })} placeholder={`${ph} in`} className={inputCls} data-testid={`config-${k}-${ci}`} />
                  </span>
                ))}
              </div>
              <div className="col-span-1 flex items-center gap-1 sm:col-span-2">
                {[["pallet_length", "Pallet L"], ["pallet_width", "W"], ["pallet_height", "H"]].map(([k, ph], j) => (
                  <span key={k} className="flex flex-1 items-center gap-1">
                    {j > 0 && <span className="text-xs text-slate-400">×</span>}
                    <Input type="text" inputMode="decimal" value={c[k]} onChange={(e) => updateConfig(ci, { [k]: e.target.value.replace(/[^0-9.]/g, "") })} placeholder={`${ph} in`} className={inputCls} data-testid={`config-${k}-${ci}`} />
                  </span>
                ))}
              </div>
              <span />
            </div>
          </div>
        ))}
        <p className="text-xs text-slate-500">Each configuration is a case-packing option; its Label appears as a toggle on the BOM &amp; when creating an order. Bottles/Case powers preforms, caps &amp; labels. Add a second configuration to offer multiple case sizes — case-level components (Level = Per case) then get a Type per configuration. Leave the weight fields blank to use the weight calculated from the BOM (you can also override / revert directly on the BOM card).</p>
      </div>

      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Bill of Materials</h3>

        {/* Column headers (desktop) */}
        <div className="hidden gap-3 px-1 pb-2 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 sm:grid sm:grid-cols-[28px_1fr_1fr_110px_120px_56px]">
          <span />
          <span>Component</span>
          <span>Type</span>
          <span>Qty</span>
          <span>Level</span>
          <span />
        </div>

        <div className="space-y-3">
          {lines.map((l, i) => {
            const comp = compById[l.component_id];
            const types = comp?.types || [];
            return (
              <div
                key={l._id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { moveTo(dragIndex, i); setDragIndex(null); }}
                className={`grid grid-cols-1 gap-3 border p-3 sm:grid-cols-[28px_1fr_1fr_180px_120px_56px] sm:items-center sm:border-0 sm:p-0 ${dragIndex === i ? "border-[#002FA7] opacity-60" : "border-slate-200"}`}
                data-testid={`bom-line-${i}`}
              >
                <div
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragEnd={() => setDragIndex(null)}
                  className="flex cursor-grab items-center justify-center text-slate-300 hover:text-slate-500 active:cursor-grabbing"
                  data-testid={`drag-handle-${i}`}
                  title="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </div>
                <div>
                  <span className="mb-1 block text-[11px] font-medium text-slate-500 sm:hidden">Component</span>
                  <Select value={l.component_id} onValueChange={(v) => updateLine(i, { component_id: v, type_id: "", type_id_12: "", type_id_24: "", basis: basisOf(compById[v]) || "per_bottle", qty_unit: defaultUnit(dimOf(compById[v])) })}>
                    <SelectTrigger className="rounded-sm border-slate-300" data-testid={`bom-component-${i}`}>
                      <SelectValue placeholder="Select component" />
                    </SelectTrigger>
                    <SelectContent>
                      {components.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <span className="mb-1 block text-[11px] font-medium text-slate-500 sm:hidden">{comp ? singular(comp.name) : "Type"}</span>
                  {isPerCaseLine(l) && packConfigs.length > 1 ? (
                    <div style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: `repeat(${packConfigs.length}, minmax(0,1fr))` }}>
                      {packConfigs.map((cfg, ci) => (
                        <Select key={cfg._id} value={l.config_type_ids?.[cfg.id] || ""} onValueChange={(v) => updateLine(i, { config_type_ids: { ...(l.config_type_ids || {}), [cfg.id]: v } })} disabled={types.length === 0}>
                          <SelectTrigger className="rounded-sm border-slate-300" data-testid={`bom-type-cfg-${i}-${ci}`}>
                            <SelectValue placeholder={`${cfg.label || `Config ${ci + 1}`} ${singular(comp.name).toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>{types.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}</SelectContent>
                        </Select>
                      ))}
                    </div>
                  ) : (
                    <Select value={l.type_id} onValueChange={(v) => updateLine(i, { type_id: v })} disabled={!comp || types.length === 0}>
                      <SelectTrigger className="rounded-sm border-slate-300" data-testid={`bom-type-${i}`}>
                        <SelectValue placeholder={!comp ? "Pick component first" : types.length === 0 ? "No types" : `Select ${singular(comp.name)} (optional)`} />
                      </SelectTrigger>
                      <SelectContent>
                        {types.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                  {(() => {
                    const sel = (comp?.types || []).find((x) => x.id === l.type_id);
                    if (!sel?.recipe?.length) return null;
                    return (
                      <p className="mt-1 text-[10px] leading-tight text-slate-400" data-testid={`bom-recipe-note-${i}`}>
                        ↳ brings {sel.recipe.map((r) => `${r.quantity} ${r.unit || ""} ${compById[r.component_id]?.name || "ingredient"}${r.basis === "per_litre_parent" ? " / L" : ""}`).join(", ")}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <span className="mb-1 block text-[11px] font-medium text-slate-500 sm:hidden">Qty</span>
                  {(() => {
                    const dim = comp ? dimOf(comp) : "count";
                    const isCount = dim === "count";
                    const curUnit = l.qty_unit || defaultUnit(dim);
                    const sys = systemOfUnit(dim, curUnit);
                    const multiQty = isPerCaseLine(l) && packConfigs.length > 1;
                    return (
                      <div className="space-y-1">
                        {multiQty ? (
                          <div style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: `repeat(${packConfigs.length}, minmax(0,1fr))` }}>
                            {packConfigs.map((cfg, ci) => (
                              <div key={cfg._id}>
                                <span className="block truncate text-[10px] text-slate-400">{cfg.label || `Config ${ci + 1}`}</span>
                                <Input type="number" step="any" min="0"
                                  value={(l.config_quantities || {})[cfg.id] ?? l.quantity}
                                  onChange={(e) => updateLine(i, { config_quantities: { ...(l.config_quantities || {}), [cfg.id]: e.target.value } })}
                                  className={inputCls} data-testid={`bom-qty-cfg-${i}-${ci}`} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Input type="number" step="any" min="0" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className={inputCls} data-testid={`bom-qty-${i}`} />
                        )}
                        {!isCount && (
                          <div className="flex items-center gap-1">
                            <div className="flex shrink-0 overflow-hidden rounded-sm border border-slate-300 text-[10px] font-semibold" data-testid={`bom-qty-system-${i}`}>
                              {["metric", "imperial"].map((s) => (
                                <button key={s} type="button" title={s === "metric" ? "Metric" : "Imperial"} onClick={() => updateLine(i, { qty_unit: unitsForDim(dim, s)[0] })} className={`px-2 py-1.5 ${sys === s ? "bg-[#002FA7] text-white" : "bg-white text-slate-500 hover:text-slate-800"}`} data-testid={`bom-qty-system-${i}-${s}`}>{s === "metric" ? "M" : "I"}</button>
                              ))}
                            </div>
                            <Select value={curUnit} onValueChange={(v) => updateLine(i, { qty_unit: v })} disabled={!comp}>
                              <SelectTrigger className="h-9 flex-1 rounded-sm border-slate-300" data-testid={`bom-qty-unit-${i}`}><span>{curUnit}</span></SelectTrigger>
                              <SelectContent>{unitsForDim(dim, sys).map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {comp && <div className="text-[10px] leading-tight text-slate-400" data-testid={`bom-unit-hint-${i}`}>{isCount ? `each ${perLabel(comp, l)}` : `${curUnit} ${perLabel(comp, l)}`}</div>}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <span className="mb-1 block text-[11px] font-medium text-slate-500 sm:hidden">Level</span>
                  <Select value={l.basis || "per_bottle"} onValueChange={(v) => updateLine(i, { basis: v })} disabled={!comp}>
                    <SelectTrigger className="h-10 rounded-sm border-slate-300" data-testid={`bom-basis-${i}`}><span>{BASIS[l.basis || "per_bottle"]}</span></SelectTrigger>
                    <SelectContent>{Object.entries(BASIS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-end sm:justify-center">
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => removeLine(i)} data-testid={`remove-bom-line-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <Button variant="outline" className="mt-3 w-full rounded-sm border-dashed sm:w-auto" onClick={addLine} data-testid="add-bom-line-button">
          <Plus className="mr-1 h-4 w-4" /> Add component
        </Button>

        {components.length === 0 && (
          <p className="mt-3 text-sm text-amber-600">You have no components yet. Add components first to build a BOM.</p>
        )}
      </div>

      {error && <p className="text-sm text-red-600" data-testid="bom-error">{error}</p>}

      <div className="flex justify-end gap-2 border-t border-slate-200 pt-6">
        <Button variant="outline" className="rounded-sm" onClick={onCancel} data-testid="bom-cancel-button">Cancel</Button>
        <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={saving} onClick={submit} data-testid="bom-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Product"}
        </Button>
      </div>
    </div>
  );
}
